import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Container, Box, Tabs, Tab, Card, CardMedia, Modal, Button, IconButton } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CloseIcon from '@mui/icons-material/Close';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import '../styles/css/Portfolio.css';
import { LanguageContext } from '../App';

// Helper function to dynamically import images
const importAll = (r) => {
  return r.keys().map((item) => ({
    src: r(item),
    category: item.split('/')[1], // Get category from folder name
  }));
};

// Use require.context to get all images
const getPortfolioItems = () => {
  try {
    // Adjust this path to match your actual structure
    const images = importAll(require.context('../styles/images/portfolio', true, /\.(png|jpe?g|webp)$/));
    return images.map((item, index) => ({
      id: index + 1,
      category: item.category,
      image: item.src,
    }));
  } catch (error) {
    console.error("Error loading images:", error);
    return [];
  }
};

const MIN_SCALE = 1;
const MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_DELAY = 300;

const getTouchDistance = (touches) => {
  const [a, b] = touches;
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
};

const Portfolio = () => {
  const [currentTab, setCurrentTab] = useState('all');
  const [openModal, setOpenModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [visibleItems, setVisibleItems] = useState([]);
  const [visibleCount, setVisibleCount] = useState(30); // Show 30 images per page
  const [filteredItems, setFilteredItems] = useState([]);
  const controls = useAnimation();
  const { language, translations } = useContext(LanguageContext);

  // Pinch-to-zoom / pan state for the lightbox
  const [zoomScale, setZoomScale] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPinching, setIsPinching] = useState(false);
  const pinchStateRef = useRef({ startDistance: 0, startScale: 1 });
  const panStateRef = useRef({ startX: 0, startY: 0, startOffsetX: 0, startOffsetY: 0 });
  const lastTapRef = useRef(0);

  useEffect(() => {
    // Load and shuffle all items
    const items = getPortfolioItems();
    const shuffled = [...items].sort(() => 0.5 - Math.random());
    setAllItems(shuffled);
  }, []);

  useEffect(() => {
    // Filter items based on current tab
    const filtered = currentTab === 'all' 
      ? allItems 
      : allItems.filter(item => item.category === currentTab);
    
    setFilteredItems(filtered);
    setVisibleItems(filtered.slice(0, visibleCount));
  }, [currentTab, allItems, visibleCount]);

  const resetZoom = useCallback(() => {
    setZoomScale(1);
    setPanOffset({ x: 0, y: 0 });
  }, []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setVisibleCount(30); // Reset to 30 when changing tabs
  };

  const handleOpenModal = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
    setOpenModal(true);
    resetZoom();
    
    // Reset animation controls when opening modal
    controls.start({
      opacity: 1,
      x: 0,
      transition: { duration: 0.3 }
    });
  };

  const handleCloseModal = useCallback(() => {
    setOpenModal(false);
    resetZoom();
  }, [resetZoom]);

  const handleLoadMore = () => {
    setVisibleCount(prevCount => prevCount + 30); // Load 30 more each time
  };

  const handleNext = useCallback(() => {
    resetZoom();
    setSelectedIndex((prevIndex) => {
      const newIndex = (prevIndex + 1) % filteredItems.length;
      setSelectedImage(filteredItems[newIndex].image);
      return newIndex;
    });
    controls.start({
      x: [-50, 0],
      opacity: [0, 1],
      transition: { duration: 0.3 }
    });
  }, [controls, filteredItems, resetZoom]);

  const handlePrev = useCallback(() => {
    resetZoom();
    setSelectedIndex((prevIndex) => {
      const newIndex = (prevIndex - 1 + filteredItems.length) % filteredItems.length;
      setSelectedImage(filteredItems[newIndex].image);
      return newIndex;
    });
    controls.start({
      x: [50, 0],
      opacity: [0, 1],
      transition: { duration: 0.3 }
    });
  }, [controls, filteredItems, resetZoom]);

  // Handle swipe gestures (only when not zoomed in)
  const handleDragEnd = (event, info) => {
    if (zoomScale !== 1) return;
    if (info.offset.x > 100) {
      handlePrev();
    } else if (info.offset.x < -100) {
      handleNext();
    }
  };

  // Pinch-to-zoom + pan + double-tap handlers
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      pinchStateRef.current.startDistance = getTouchDistance(e.touches);
      pinchStateRef.current.startScale = zoomScale;
      setIsPinching(true);
    } else if (e.touches.length === 1) {
      if (zoomScale > 1) {
        panStateRef.current = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startOffsetX: panOffset.x,
          startOffsetY: panOffset.y,
        };
      }
      const now = Date.now();
      if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
        if (zoomScale > 1) {
          resetZoom();
        } else {
          setZoomScale(DOUBLE_TAP_SCALE);
        }
      }
      lastTapRef.current = now;
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const newDistance = getTouchDistance(e.touches);
      const ratio = newDistance / (pinchStateRef.current.startDistance || newDistance);
      const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchStateRef.current.startScale * ratio));
      setZoomScale(newScale);
    } else if (e.touches.length === 1 && zoomScale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - panStateRef.current.startX;
      const dy = e.touches[0].clientY - panStateRef.current.startY;
      setPanOffset({
        x: panStateRef.current.startOffsetX + dx,
        y: panStateRef.current.startOffsetY + dy,
      });
    }
  };

  const handleTouchEnd = (e) => {
    setIsPinching(false);
    if (e.touches.length === 0 && zoomScale < 1.02) {
      resetZoom();
    }
  };

  const handleDoubleClick = () => {
    if (zoomScale > 1) {
      resetZoom();
    } else {
      setZoomScale(DOUBLE_TAP_SCALE);
    }
  };

  const handleLightboxCta = () => {
    setOpenModal(false);
    resetZoom();
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 200);
  };

  // Keyboard navigation for desktop
  useEffect(() => {
    if (!openModal) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') {
        language === 'he' ? handlePrev() : handleNext();
      } else if (e.key === 'ArrowLeft') {
        language === 'he' ? handleNext() : handlePrev();
      } else if (e.key === 'Escape') {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [openModal, language, handleNext, handlePrev, handleCloseModal]);

  // Get all unique category keys from items for dynamic tab generation
  const categories = [...new Set(allItems.map(item => item.category))];
  const hasMoreItems = visibleItems.length < filteredItems.length;

  // Animation variants for grid items. Each item animates independently
  // (rather than the whole grid unmounting/remounting) so the section never
  // collapses to zero height while photos transition between categories.
  const itemVariants = {
    hidden: { y: 24, opacity: 0, scale: 0.94 },
    visible: (index = 0) => ({
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 120,
        damping: 16,
        delay: Math.min(index % 12, 12) * 0.03
      }
    }),
    exit: {
      y: -12,
      opacity: 0,
      scale: 0.9,
      transition: { duration: 0.2, ease: 'easeIn' }
    }
  };

  const t = translations[language];

  return (
    <Container className="portfolio-container" maxWidth={false} sx={{ padding: 0 }}>
      <h2 className="section-title">
        {t.portfolioTitle}
      </h2>
      <p className="section-subtitle">
        {t.portfolioSubtitle}
      </p>
      
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}           
          scrollButtons="auto"
          className="portfolio-tabs"
          variant="scrollable"
          allowScrollButtonsMobile
          ScrollButtonComponent={IconButton}
          TabIndicatorProps={{
            style: { display: 'none' }
          }}
          sx={{ 
            flexWrap: 'wrap',
            '& .MuiTabs-flexContainer': {
              flexWrap: 'wrap',
              justifyContent: 'center'
            }
          }}
        >
          <Tab 
            className={`category ${currentTab === 'all' ? 'active-tab' : ''}`} 
            label={language === 'he' ? 'הכל' : 'All'} 
            value="all" 
          />
          {categories.map(category => (
            <Tab 
              className={`category ${currentTab === category ? 'active-tab' : ''}`}
              key={category} 
              label={t.portfolioCategories[category] || category}
              value={category} 
            />
          ))}
        </Tabs>
      </Box>
      
      <div className="portfolio-masonry">
        <AnimatePresence>
          {visibleItems.map((item, index) => (
            <motion.div
              layout
              custom={index}
              variants={itemVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              key={item.id}
              className="portfolio-masonry-item"
            >
              <Card 
                className="portfolio-item" 
                onClick={() => handleOpenModal(item.image, filteredItems.findIndex(i => i.id === item.id))}
              >
                <CardMedia
                  component="img"
                  image={item.image}
                  alt={`${t.portfolioImageAlt} ${index + 1}`}
                  className="portfolio-image"
                  loading="lazy"
                />
                <Box className="portfolio-overlay" />
                {t.portfolioCategories[item.category] && (
                  <span className="portfolio-tag">
                    {t.portfolioCategories[item.category]}
                  </span>
                )}
              </Card>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
      
      {hasMoreItems && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleLoadMore}
            className="load-more-button"
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t.loadMoreBtn}
          </Button>
        </Box>
      )}
      
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        className="portfolio-modal"
      >
        <Box className="modal-content">
          <div className="modal-backdrop"></div>
          
          <IconButton 
            className="modal-close"
            onClick={handleCloseModal}
            sx={{ position: 'absolute', top: 10, right: 10, color: 'white', zIndex: 1200 }}
          >
            <CloseIcon />
          </IconButton>

          {filteredItems.length > 0 && (
            <Box className="modal-counter">
              {selectedIndex + 1} / {filteredItems.length}
            </Box>
          )}
          
          <IconButton 
            className="modal-prev hide-on-mobile"
            onClick={handlePrev}
            sx={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 1200 }}
          >
            <NavigateBeforeIcon fontSize="large" />
          </IconButton>

          <div
            className="modal-image-zoom-wrapper"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoomScale})`,
              transition: isPinching ? 'none' : 'transform 0.25s ease-out',
              touchAction: 'none',
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onDoubleClick={handleDoubleClick}
          >
            <motion.img 
              src={selectedImage} 
              alt="Full size" 
              initial={{ opacity: 1, x: 0 }}
              animate={controls}
              transition={{ duration: 0.3 }}
              drag={zoomScale === 1 ? "x" : false}
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.7}
              onDragEnd={handleDragEnd}
              className="modal-image"
              draggable={false}
            />
          </div>
          
          <IconButton 
            className="modal-next hide-on-mobile"
            onClick={handleNext}
            sx={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'white', zIndex: 1200 }}
          >
            <NavigateNextIcon fontSize="large" />
          </IconButton>

          <motion.button
            type="button"
            className="lightbox-cta"
            onClick={handleLightboxCta}
            whileHover={{ scale: 1.04 }}
            whileTap={{ scale: 0.97 }}
          >
            <WhatsAppIcon fontSize="small" />
            <span className="lightbox-cta__text">
              <strong>{t.lightboxCtaText}</strong>
              <small>{t.lightboxCtaSubtext}</small>
            </span>
          </motion.button>
        </Box>
      </Modal>
    </Container>
  );
}

export default Portfolio;
